import { GovIcon } from '@gov-design-system-ce/react';
import { GovFormWeb } from '@gov-design-system-ce/react-form';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { addWhen, pipe } from '@gov-nx/core/types';
import { useRegistrSVSeznamContext } from '@gov-nx/module/page';
import {
	Button,
	ContentLayout,
	ErrorStatusBanner,
	FilterParameterList,
	FormBadges,
	FormSearch,
	FormWidthElementLayout,
	HeaderLayout,
	InfoBanner,
	KeyValues,
	Pagination,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { pageRouteResolver, scrollToTop } from '@gov-nx/web';
import { FilterModal } from './FilterModal';
import {
	RegisterSVListAndFilterSkeleton,
	RegisterSVListSkeleton,
} from './RegisterSVListSkeleton';

export const RegisterSVListView = () => {
	const {
		code,
		controls,
		totalFilteredVehicles,
		vehiclesPaginated,
		showNoVehiclesBanner,
		vehicleGetters,
		formDefinition,
		openFilterModal,
		onSubmit,
		onReset,
		filterParams,
		ico,
		filter,
		setPartialFilter,
	} = useRegistrSVSeznamContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);

	if (controls.initialLoading) {
		return <RegisterSVListAndFilterSkeleton />;
	}

	if (controls.initialError) {
		return <ErrorStatusBanner code={code} />;
	}

	return (
		<ContentLayout>
			<GovFormWeb
				formDefinition={formDefinition}
				onSubmit={onSubmit}>
				<FilterModal />

				<HeaderLayout
					className="flex justify-between"
					filterParams={
						<FilterParameterList
							items={filterParams}
							onReset={onReset}
						/>
					}>
					<FormWidthElementLayout
						type="l"
						className="relative">
						<FormSearch
							input={{
								field: {
									name: 'filterText',
									placeholder: tsn('formular.placeholder.filter-text'),
								},
								label: {
									children: tsn('filtr.pole.wcag.vyhledat-vozidlo'),
								},
							}}
							button={{
								'aria-label': tsn('akce.wcag.vyhledat'),
							}}
						/>
					</FormWidthElementLayout>

					<Button
						onClick={openFilterModal}
						color="primary"
						type="outlined"
						size="m"
						className="ml-auto"
						aria-label={tsn('akce.wcag.zobrazit-filtr')}>
						<GovIcon
							name="funnel"
							type="templates"
						/>
					</Button>
				</HeaderLayout>

				<div className="pt-(--templates-margin-m)">
					<FormBadges
						field={{
							name: 'filterVehicleStatus',
						}}
						options={[
							{
								name: 'actual',
								label: tsn('formular.pole.filtr-aktualnost-aktualni'),
							},
							{
								name: 'historical',
								label: tsn('formular.pole.filtr-aktualnost-historie'),
							},
						]}
					/>
				</div>

				{controls.resultsLimitExceeded && (
					<InfoBanner text={controls.resultsLimitExceeded} />
				)}
			</GovFormWeb>
			{controls.processLoading ? (
				<RegisterSVListSkeleton />
			) : showNoVehiclesBanner ? (
				<InfoBanner
					headline={tsn('formular.zprava.nemate-zadne-vozidlo.titulek')}
					text={tsn('formular.zprava.nemate-zadne-vozidlo.popis')}
				/>
			) : (
				<>
					<Tiles>
						{vehiclesPaginated.map(vehicleGetters).map((vehicle) => (
							<Tile
								key={vehicle.id}
								name={vehicle.name()}
								to={pipe(
									pageRouteResolver('registr-SV.detail', {
										vehicleId: vehicle.id,
									}),
									(url) => (ico ? `${url}?ico=${ico}` : url)
								)}
								icon={{ name: 'chevron-right', type: 'components' }}>
								<p className="text-(--text-secondary)">
									<KeyValues
										options={pipe(
											[
												{
													label: tsn('formular.klic.rz'),
													value: vehicle.rz(),
												},
											],
											addWhen(vehicle.isActive(), {
												label: tsn('formular.klic.status'),
												value: vehicle.statusName(),
											})
										)}
									/>
								</p>
							</Tile>
						))}
					</Tiles>
					{totalFilteredVehicles > (filter.pageSize ?? 0) ? (
						<Pagination
							color="primary"
							size="m"
							pageSize={filter.pageSize}
							current={filter.page}
							onPage={(event) => {
								scrollToTop();
								setPartialFilter({ page: event });
							}}
							total={totalFilteredVehicles}
						/>
					) : null}
				</>
			)}
		</ContentLayout>
	);
};
